import { nomeComRepresentante } from './nomeDoRepresentante';

/**
 * O rótulo de quem não tem responsável NEM departamento.
 *
 * Constante porque três lugares o escrevem: o nome na listagem, o título do
 * grupo no kanban e o agregado do servidor. Escrito à mão em cada um, basta
 * um acento diferente para a tela mostrar dois grupos que são o mesmo.
 */
export const SEM_RESPONSAVEL = 'Não atribuído';

/** Status de conta inativa (sem acesso) — alinhado ao backend User::STATUS_INACTIVE */
export const USER_STATUS_INACTIVE = 4;

export function isActiveTicketAssignee(assignee) {
    if (!assignee || assignee.id == null || assignee.id === '') return false;
    if (assignee.status == null || assignee.status === '') return true;
    return Number(assignee.status) !== USER_STATUS_INACTIVE;
}

export function getTicketAssigneeGroupKey(ticket) {
    if (isActiveTicketAssignee(ticket?.assigned_to)) {
        return ticket.assigned_to.id;
    }

    /*
     * Sem responsável, o DEPARTAMENTO separa os grupos (ponto 24 da Gisele,
     * 20/10/2026).
     *
     * Antes todos os chamados sem responsável caíam num balde só chamado "Não
     * atribuído". Desde 19/10 atribuir a um departamento zera o `assigned_to`
     * de propósito e grava `service_group_id` -- então esse balde passou a
     * misturar o que espera o Suporte, o Comercial e a Implantação, sem jeito
     * de distinguir. Era exatamente o que ela não conseguia ver.
     *
     * O prefixo mantém esses grupos longe dos ids de pessoa: `assigned_to.id`
     * é número, e um `service_group_id` igual a ele colidiria.
     */
    const departamento = ticket?.service_group_id;

    return (departamento == null || departamento === '')
        ? 'unassigned'
        : `unassigned:${departamento}`;
}

/** O grupo é de chamados sem responsável (com ou sem departamento)? */
export function ehGrupoSemResponsavel(assigneeKey) {
    return String(assigneeKey || '') === 'unassigned'
        || String(assigneeKey || '').startsWith('unassigned:');
}

/**
 * @param {object} ticket
 * @param {object} representantes  mapa `codigo -> nome`, de `useRepresentantes`.
 *   Opcional: sem ele o nome sai como sempre saiu. É o que faz a falha da
 *   requisição do mapa degradar para o comportamento antigo, em vez de
 *   esvaziar o rótulo.
 */
export function getTicketAssigneeDisplayName(ticket, representantes = null) {
    if (!isActiveTicketAssignee(ticket?.assigned_to)) {
        return SEM_RESPONSAVEL;
    }

    const nome = ticket.assigned_to.name || SEM_RESPONSAVEL;
    const codigo = ticket.assigned_to.representante_codigo;

    if (!representantes || codigo == null || codigo === '') return nome;

    return nomeComRepresentante(nome, representantes[String(codigo)]);
}

/**
 * O título do grupo no kanban: o NOME DO DEPARTAMENTO quando não há
 * responsável.
 *
 * Separado de `getTicketAssigneeDisplayName` de propósito, e não é zelo: a
 * função original alimenta também uma CONDIÇÃO DE FILTRO na tela de chamados
 * (`TicketPage`, operador "igual a"). Mexer no nome lá faria um filtro por
 * "Não atribuído" deixar de casar com os chamados que ele sempre pegou --
 * silenciosamente, que é como o filtro de kanban já ficou vazio uma vez.
 *
 * ## Por que o departamento SUBSTITUI "Não atribuído"
 *
 * Em 20/10 (ponto 24) o rótulo virou "Não atribuído — Comercial". O dono do
 * produto pediu em 23/10 que fosse só "Comercial": "criar igual é para cada
 * operador no kanban, que mostra ali um expansível com o nome, mas também
 * para o nome do departamento -- ao invés de não atribuído".
 *
 * Faz sentido no quadro: cada faixa é "de quem é esta fila", e a resposta é
 * o Comercial do mesmo jeito que é a Ana. Que ninguém assumiu ainda é o que
 * a coluna de status já diz.
 *
 * Sem departamento, "Não atribuído" continua -- é literalmente o que ele
 * descreve. Esta regra tem de concordar com a do servidor
 * (`TicketController::assigneeCounts`), que nomeia os mesmos grupos pelo
 * outro caminho: o agregado monta a faixa, e a busca monta a mesma faixa
 * aqui. Duas respostas diferentes seriam a mesma tela mudando de nome
 * conforme a busca estivesse ligada.
 *
 * @param {object} ticket
 * @param {object} representantes  mapa de `useRepresentantes`
 * @param {object} departamentos   mapa de `useDepartamentos` (id -> descrição)
 */
export function rotuloDoResponsavelComDepartamento(ticket, representantes = null, departamentos = null) {
    if (isActiveTicketAssignee(ticket?.assigned_to)) {
        return getTicketAssigneeDisplayName(ticket, representantes);
    }

    const id = ticket?.service_group_id;

    if (id == null || id === '') return SEM_RESPONSAVEL;

    // Sem o mapa (ainda carregando, ou a requisição falhou) o rótulo volta a
    // ser o de antes. É o degrau certo para cair: some a informação nova,
    // nunca a antiga.
    return (departamentos || {})[String(id)] || SEM_RESPONSAVEL;
}

/** Normaliza ticket para listagens quando o responsável está inativo (mantém ticket, sem assignee). */
export function normalizeTicketAssigneeForList(ticket) {
    if (!ticket || isActiveTicketAssignee(ticket.assigned_to)) {
        return ticket;
    }
    return { ...ticket, assigned_to: null };
}
